import { useState } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PublicFooter from "@/components/PublicFooter";
import { GoogleButton } from "@/components/GoogleButton";
import CurrencySelect from "@/components/CurrencySelect";
import { useCurrency } from "@/context/CurrencyContext";

// Patch 63 Part 1 — after login, land the user directly in the Split-View Canvas (their most recent
// project's builder view). Falls back to the Workspace home if they have no project yet. Navigation
// is otherwise unchanged.
async function goSplitView(me, nav) {
  // Post-login always lands in the Split-View Canvas (BuildHome resolves existing-project vs empty state).
  nav("/workspace/build");
}

function AuthCard({ title, children, sub }) {
  return (
    <div className="k-canvas" style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 0" }}>
        <div className="k-container k-fadein" style={{ width: 420, padding: 34 }}>
          <Link to="/" className="k-heading" style={{ textDecoration: "none", display: "inline-block" }}>
            <span style={{ fontWeight: 700, fontSize: 22, display: "inline-block", borderBottom: "3px solid var(--accent-primary)", paddingBottom: 3 }}>Kadana</span>
          </Link>
          <h1 className="k-heading" style={{ fontSize: 24, margin: "20px 0 6px" }}>{title}</h1>
          {sub && <p className="k-muted" style={{ fontSize: 14, marginTop: 0 }}>{sub}</p>}
          {children}
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}

const label = { fontSize: 13, color: "var(--muted)", display: "block", margin: "14px 0 6px" };
const errStyle = { color: "var(--danger)", fontSize: 13, marginTop: 12 };

export function Login() {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [mfa, setMfa] = useState(""); const [needMfa, setNeedMfa] = useState(false);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const { setUser, refresh } = useAuth(); const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/auth/login", { email, password, mfa_code: mfa || null });
      const me = await refresh(); await goSplitView(me, nav);
    } catch (e2) {
      const msg = errMsg(e2);
      if (msg.includes("MFA")) setNeedMfa(true);
      setErr(msg);
    } finally { setBusy(false); }
  };
  return (
    <AuthCard title="Sign in">
      <form onSubmit={submit}>
        <label style={label}>Email</label>
        <input className="k-control" data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
        <label style={label}>Password</label>
        <input className="k-control" data-testid="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
        {needMfa && (<><label style={label}>MFA code</label>
          <input className="k-control" data-testid="login-mfa" value={mfa} onChange={e => setMfa(e.target.value)} /></>)}
        {err && <div style={errStyle} data-testid="login-error">{err}</div>}
        <button className="k-btn k-btn-primary" data-testid="login-submit" style={{ width: "100%", marginTop: 20 }} disabled={busy}>
          {busy ? "…" : "Sign in"}
        </button>
      </form>
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "18px 0 12px" }}>
        <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
        <span className="k-muted" style={{ fontSize: 12 }}>or</span>
        <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
      </div>
      <GoogleButton />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16, fontSize: 13 }}>
        <Link className="k-link" to="/forgot-password" data-testid="forgot-link">Forgot password?</Link>
        <Link className="k-link" to="/register" data-testid="to-register">Create account</Link>
      </div>
    </AuthCard>
  );
}

export function Register() {
  const loc = useLocation();
  const carried = loc.state || {};
  const [f, setF] = useState({ name: "", email: "", password: "", country: "GB",
    phone: "", whatsapp: "", over_18: false, accept_aup: false });
  const [sameWa, setSameWa] = useState(false);
  const [waConsent, setWaConsent] = useState(false); // separate, defaults OFF
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const { refresh } = useAuth(); const nav = useNavigate();
  const { format: curFormat } = useCurrency();

  const strength = (() => {
    const p = f.password; let s = 0;
    if (p.length >= 8) s++; if (/[A-Z]/.test(p)) s++; if (/[0-9]/.test(p)) s++; if (/[^A-Za-z0-9]/.test(p)) s++;
    return s; // 0-4
  })();
  const strengthLabel = ["Too short", "Weak", "Fair", "Good", "Strong"][strength];
  const strengthColor = ["var(--danger)", "var(--danger)", "var(--warning)", "var(--accent)", "var(--success)"][strength];

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/auth/register", {
        name: f.name, email: f.email, password: f.password, country: f.country,
        over_18: f.over_18, accept_aup: f.accept_aup,
      });
      if (waConsent) { try { await api.post("/consents", { accept_analytics: false, accept_marketing: true }); } catch {} }
      await refresh(); nav("/workspace/build");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <AuthCard title="Start free" sub="100 free credits, no card needed">
      {carried.prompt && <div className="k-badge k-badge-accent" style={{ marginBottom: 12 }} data-testid="carried-prompt">Your idea is carried over</div>}
      {carried.price_range && (
        <div className="k-control" data-testid="carried-price-range"
          style={{ marginBottom: 14, padding: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span className="k-muted" style={{ fontSize: 13 }}>
            Indicative fixed-price range: {curFormat(carried.price_range.gbp_low, { decimals: 0 })}–{curFormat(carried.price_range.gbp_high, { decimals: 0 })}
          </span>
          <CurrencySelect compact />
        </div>
      )}
      <GoogleButton />
      <div className="k-muted" style={{ fontSize: 12, textAlign: "center", margin: "12px 0" }}>or with email</div>
      <form onSubmit={submit}>
        <label style={label}>Name</label>
        <input className="k-control" data-testid="reg-name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} required />
        <label style={label}>Email</label>
        <input className="k-control" data-testid="reg-email" type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} required />
        <label style={label}>Password</label>
        <input className="k-control" data-testid="reg-password" type="password" value={f.password} onChange={e => setF({ ...f, password: e.target.value })} required />
        {f.password && (
          <div style={{ marginTop: 6 }} data-testid="strength-meter">
            <div style={{ height: 5, borderRadius: 3, background: "var(--control)" }}>
              <div style={{ width: `${(strength / 4) * 100}%`, height: 5, borderRadius: 3, background: strengthColor, transition: "width .2s" }} />
            </div>
            <span style={{ fontSize: 12, color: strengthColor }}>{strengthLabel}</span>
          </div>
        )}
        <label style={label}>Phone number (optional)</label>
        <input className="k-control" data-testid="reg-phone" value={f.phone}
          onChange={e => setF({ ...f, phone: e.target.value, whatsapp: sameWa ? e.target.value : f.whatsapp })} />
        <label style={label}>WhatsApp number (optional)</label>
        <input className="k-control" data-testid="reg-whatsapp" value={f.whatsapp} disabled={sameWa}
          onChange={e => setF({ ...f, whatsapp: e.target.value })} />
        <label style={{ ...label, display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
          <input type="checkbox" data-testid="reg-same-wa" checked={sameWa}
            onChange={e => { setSameWa(e.target.checked); if (e.target.checked) setF({ ...f, whatsapp: f.phone }); }} /> Same as phone number
        </label>
        <p className="k-muted" style={{ fontSize: 12, marginTop: 14 }}>We'll only message you here if you opt in below.</p>
        <label style={{ ...label, display: "flex", gap: 8, alignItems: "flex-start" }}>
          <input type="checkbox" data-testid="reg-wa-consent" checked={waConsent} onChange={e => setWaConsent(e.target.checked)} />
          <span>Send me build updates by WhatsApp or SMS.</span>
        </label>
        <label style={{ ...label, display: "flex", gap: 8, alignItems: "flex-start", marginTop: 10 }}>
          <input type="checkbox" data-testid="reg-compliance" checked={f.over_18 && f.accept_aup}
            onChange={e => setF({ ...f, over_18: e.target.checked, accept_aup: e.target.checked })} />
          <span style={{ fontSize: 12.5 }}>I accept the <a className="k-link" href="/legal/terms" target="_blank" rel="noreferrer">Terms of Service</a> and{" "}
            <a className="k-link" href="/legal/privacy" target="_blank" rel="noreferrer">Privacy notice</a>, confirm I am 18 or over, and understand
            Kadana is a software generation platform provided by Melian Dialogue Limited (Company Number PVT-27ULY98K) for building, testing, previewing, hosting and deploying software applications.</span>
        </label>
        {err && <div style={errStyle} data-testid="reg-error">{err}</div>}
        <button className="k-btn k-btn-primary" data-testid="reg-submit" style={{ width: "100%", marginTop: 18 }} disabled={busy}>
          {busy ? "…" : "Start building"}
        </button>
      </form>
      <div style={{ marginTop: 16, fontSize: 13 }}>
        <Link className="k-link" to="/login">Already have an account? Sign in</Link>
      </div>
    </AuthCard>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState(""); const [done, setDone] = useState(false);
  const submit = async (e) => { e.preventDefault(); try { await api.post("/auth/forgot-password", { email }); } catch {} setDone(true); };
  return (
    <AuthCard title="Reset password">
      {done ? <p className="k-muted" data-testid="forgot-done">If that email is registered, a reset link has been sent.</p> :
        <form onSubmit={submit}>
          <label style={label}>Email</label>
          <input className="k-control" data-testid="forgot-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          <button className="k-btn k-btn-primary" data-testid="forgot-submit" style={{ width: "100%", marginTop: 18 }}>Send reset link</button>
        </form>}
      <div style={{ marginTop: 16, fontSize: 13 }}><Link className="k-link" to="/login">Back to sign in</Link></div>
    </AuthCard>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams(); const token = params.get("token") || "";
  const [password, setPassword] = useState(""); const [err, setErr] = useState(""); const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/auth/reset-password", { token, password }); nav("/login"); }
    catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <AuthCard title="Choose a new password">
      <form onSubmit={submit}>
        <label style={label}>New password</label>
        <input className="k-control" data-testid="reset-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
        {err && <div style={errStyle} data-testid="reset-error">{err}</div>}
        <button className="k-btn k-btn-primary" data-testid="reset-submit" style={{ width: "100%", marginTop: 18 }}>Reset password</button>
      </form>
      <div style={{ marginTop: 16, fontSize: 13 }}><Link className="k-link" to="/login">Back to sign in</Link></div>
    </AuthCard>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams(); const [msg, setMsg] = useState("Verifying…");
  const nav = useNavigate();
  useState(() => {
    api.post("/auth/verify-email", { token: params.get("token") })
      .then(() => setMsg("Email verified. You can continue."))
      .catch(() => setMsg("Invalid or expired verification link."));
  });
  return (
    <AuthCard title="Email verification">
      <p className="k-muted" data-testid="verify-msg">{msg}</p>
      <button className="k-btn k-btn-primary" style={{ marginTop: 12 }} onClick={() => nav("/workspace/build")}>Continue</button>
    </AuthCard>
  );
}
